/**
 * Floating onboarding checklist (bottom-right). Always visible while
 * the tour is in flight — gives users a recover surface if they
 * accidentally dismiss a tooltip and want to know where they are.
 */
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useOnboarding } from './OnboardingContext';

export function OnboardingChecklist(): JSX.Element | null {
  const { state, steps, currentIndex, totalSteps, dismiss, restart, isActive } = useOnboarding();
  const [open, setOpen] = useState(false);

  if (!state || state.dismissedAt || state.completedAt) return null;
  if (!isActive && state.completedSteps.length === 0) return null;

  const completed = new Set(state.completedSteps);
  const pct = Math.round((completed.size / Math.max(totalSteps, 1)) * 100);

  return (
    <div className={cn('fixed bottom-24 right-4 z-[9980] md:bottom-6', 'flex flex-col items-end gap-2')}>
      {open && (
        <div
          className={cn(
            'w-72 overflow-hidden rounded-xl border border-line bg-surface shadow-elev-3',
            'flex flex-col animate-[fadeSlideUp_180ms_ease-out]',
          )}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <span className="text-sm font-semibold text-ink">Tour de configuração</span>
            <button onClick={() => setOpen(false)} className="text-xs text-ink-subtle hover:text-ink">
              ✕
            </button>
          </div>
          <div className="border-b border-line px-4 py-3">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs text-ink-muted">{completed.size} de {totalSteps} concluídas</span>
              <span className="text-xs font-semibold text-brand-400">{pct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-line">
              <div className="h-full rounded-full bg-brand-400 transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto px-4 py-3">
            {steps.map((step, i) => {
              const done = completed.has(step.id);
              const isCurrent = i === currentIndex;
              return (
                <li
                  key={step.id}
                  className={cn(
                    'flex items-start gap-2 text-sm',
                    done ? 'text-ink-subtle line-through'
                      : isCurrent ? 'font-medium text-ink'
                        : 'text-ink-muted',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border text-[10px]',
                      done ? 'border-ok bg-ok text-surface'
                        : isCurrent ? 'border-brand-400 bg-brand-400/10 text-ink'
                          : 'border-line',
                    )}
                  >
                    {done ? '✓' : i + 1}
                  </span>
                  {step.title}
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between border-t border-line px-4 py-3">
            <button onClick={dismiss} className="text-xs text-ink-muted hover:text-ink">Fechar tour</button>
            <button onClick={restart} className="text-xs text-brand-400 hover:underline">Recomeçar</button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-2 rounded-full px-4 py-2.5',
          'bg-brand-400 text-ink text-sm font-semibold shadow-elev-2',
          'hover:opacity-95 active:scale-95 transition-transform',
        )}
        aria-label="Abrir checklist de onboarding"
      >
        <span className="text-base">🗺️</span>
        <span className="hidden sm:inline">Tour</span>
        <span className="ml-1 rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-bold text-brand-400">
          {Math.max(0, totalSteps - completed.size)}
        </span>
      </button>
    </div>
  );
}
